import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";
import { clampServings } from "./recipeConstants";
import { syncPrepNotifications } from "./prepScheduler";

/**
 * Loads the user's (and family's) scheduled meals in a window.
 * Params: userId, { fromISO, toISO, familyKey, onRealtime } — fromISO/toISO
 * are ISO timestamps for the window; familyKey bumps re-queries when the
 * family set changes; onRealtime(event, meal, oldMeal) fires for events
 * originating from another user (so callers can raise toasts).
 *
 * Returns:
 *   {
 *     meals, loading, error, refresh,
 *     schedule({ recipeSlug, scheduledFor, notificationSettings, note, cookId, isRequest }),
 *     updateMeal(id, patch),
 *     claim(id),     -- set cook_id = me (accept a request)
 *     unclaim(id),   -- set cook_id = null (back into request state)
 *     cancel(id),
 *   }
 *
 * Meals are kept sorted by scheduled_for ascending.
 *
 * Attribution columns (all nullable for backward-compat):
 *   user_id       — creator (always set)
 *   cook_id       — who's going to cook. null = unclaimed request.
 *   requested_by  — only set for request-a-meal rows (normally = user_id)
 *
 * Prep reminder sync:
 *   Pass a `recipeResolver` option — a function from slug → recipe
 *   object — and schedule()/updateMeal() will automatically upsert
 *   prep_notifications rows for the meal's recipe.prepSteps. The
 *   reminders fire for the cook (cook_id, falling back to user_id).
 *   See src/lib/prepScheduler.js.
 */
export function useScheduledMeals(userId, { fromISO, toISO, familyKey, onRealtime, recipeResolver } = {}) {
  const recipeResolverRef = useRef(recipeResolver);
  useEffect(() => { recipeResolverRef.current = recipeResolver; }, [recipeResolver]);
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) {
      setMeals([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let q = supabase.from("scheduled_meals").select("*");
    if (fromISO) q = q.gte("scheduled_for", fromISO);
    if (toISO)   q = q.lte("scheduled_for", toISO);
    const { data, error: e } = await q.order("scheduled_for", { ascending: true });
    if (e) setError(e);
    setMeals(data || []);
    setLoading(false);
  }, [userId, fromISO, toISO]);

  useEffect(() => { load(); }, [load, familyKey]);

  // Keep onRealtime in a ref so identity changes don't tear down the channel.
  const onRealtimeRef = useRef(onRealtime);
  useEffect(() => { onRealtimeRef.current = onRealtime; }, [onRealtime]);

  // Realtime. Same approach as useSyncedList — merge other-user events and
  // reconcile our own writes.
  useEffect(() => {
    if (!userId) return;
    const ch = safeChannel(`rt:scheduled_meals:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "scheduled_meals" }, (payload) => {
        const row    = payload.new && Object.keys(payload.new).length ? payload.new : null;
        const oldRow = payload.old && Object.keys(payload.old).length ? payload.old : null;
        const fromOther =
          (row && row.user_id && row.user_id !== userId) ||
          (oldRow && oldRow.user_id && oldRow.user_id !== userId);

        setMeals(prev => {
          if (payload.eventType === "INSERT") {
            if (prev.some(m => m.id === row.id)) return prev;
            const next = [...prev, row];
            next.sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
            return next;
          }
          if (payload.eventType === "UPDATE") {
            if (!prev.some(m => m.id === row.id)) return prev;
            // Merge guard: TOAST-stored jsonb (recipe_snapshot, set
            // by migration 0164) is not always replicated in
            // realtime UPDATE payloads. If the incoming row is
            // missing the snapshot but our local copy has one,
            // keep the local copy — wholesale replace would
            // silently strip it and the calendar tile would lose
            // its point-in-time pin.
            return prev.map(m => {
              if (m.id !== row.id) return m;
              const snapshot =
                row.recipe_snapshot && typeof row.recipe_snapshot === "object"
                  ? row.recipe_snapshot
                  : m.recipe_snapshot;
              return { ...row, recipe_snapshot: snapshot };
            });
          }
          if (payload.eventType === "DELETE") {
            const id = oldRow?.id;
            return id ? prev.filter(m => m.id !== id) : prev;
          }
          return prev;
        });

        const cb = onRealtimeRef.current;
        if (fromOther && cb) cb(payload.eventType, row, oldRow);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId]);

  const schedule = useCallback(
    async (args) => {
      const data = await insertScheduledMeal({
        userId,
        recipeResolver: recipeResolverRef.current,
        ...args,
      });
      setMeals(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        const next = [...prev, data];
        next.sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
        return next;
      });
      return data;
    },
    [userId]
  );

  const updateMeal = useCallback(async (id, patch) => {
    // Same clamp as schedule() — MealDetailDrawer's servings stepper
    // patches through here and must respect the DB's 1–100 check.
    if (patch && patch.servings != null) {
      patch = { ...patch, servings: clampServings(patch.servings) };
    }
    const { data, error: e } = await supabase
      .from("scheduled_meals")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (e) throw e;
    setMeals(prev => prev.map(m => (m.id === id ? data : m)));
    // Reschedule / cook-change / toggle-flip — all want the prep queue
    // rebuilt. syncPrepNotifications deletes and re-inserts, so running
    // it on unrelated patches (e.g. `note`) is harmless.
    const resolver = recipeResolverRef.current;
    const cookUserId = data.cook_id || data.user_id;
    if (resolver && cookUserId) {
      const recipe = resolver(data.recipe_slug, data.recipe_user_id);
      if (recipe) {
        syncPrepNotifications({ scheduledMeal: data, recipe, userId: cookUserId });
      }
    }
    return data;
  }, []);

  const claim = useCallback(async (id) => {
    return updateMeal(id, { cook_id: userId });
  }, [updateMeal, userId]);

  const unclaim = useCallback(async (id) => {
    return updateMeal(id, { cook_id: null });
  }, [updateMeal]);

  const cancel = useCallback(async (id) => {
    const { error: e } = await supabase.from("scheduled_meals").delete().eq("id", id);
    if (e) throw e;
    setMeals(prev => prev.filter(m => m.id !== id));
  }, []);

  return { meals, loading, error, refresh: load, schedule, updateMeal, claim, unclaim, cancel };
}

// Standalone schedule writer — the hook's schedule() delegates here.
//
// Exists so surfaces that DON'T have the full hook mounted (the
// weekly planner opened from the + / Quick Create tab, where the
// Calendar tab is unmounted) can still schedule meals. Mounting a
// second useScheduledMeals instance is NOT safe while the Calendar
// is up: safeChannel() steals the `rt:scheduled_meals:<uid>` topic
// from the prior mount, killing the Calendar's realtime. A plain
// writer has no channel, so it composes anywhere.
export async function insertScheduledMeal({
  userId, recipeResolver = null,
  recipeSlug, recipeUserId, scheduledFor, notificationSettings = {},
  note = null, cookId, isRequest = false, servings, mealSlot,
  fromPantryRowId, eatAnchor = false, day2Hour = null,
  recipeSnapshot = null, weeklyPlanId = null,
}) {
  if (!userId) throw new Error("schedule called without a userId");
  // If caller didn't specify, default to self-cooking unless it's a request.
  const effectiveCookId = cookId !== undefined ? cookId : (isRequest ? null : userId);
  // Resolve the user_recipes row at schedule time so we can snapshot
  // it onto the meal row (migration 0164). Bundled recipes (no
  // recipeUserId) skip the snapshot — they're in the source tree,
  // can't drift. Caller-supplied snapshot wins over the resolver-
  // derived one — the weekly planner schedules CONCEPT slots whose
  // pseudo-recipe exists nowhere but the snapshot.
  const snapshotRecipe = recipeSnapshot
    || (recipeUserId && recipeResolver ? recipeResolver(recipeSlug, recipeUserId) : null);

  const row = {
    user_id: userId,
    recipe_slug: recipeSlug,
    scheduled_for: scheduledFor,
    notification_settings: notificationSettings,
    note,
    cook_id: effectiveCookId,
    requested_by: isRequest ? userId : null,
    // Conditional spreads so older DBs (pre-0069 / pre-0120 / pre-
    // 0139) don't 400 on unknown columns. Servings clamped to the
    // DB's 1–100 check (0278) so out-of-range degrades to the bound.
    ...(servings != null ? { servings: clampServings(servings) } : {}),
    ...(mealSlot ? { meal_slot: mealSlot } : {}),
    // Leftover reference — day-of UI opens I-ate-this directly. 0120.
    ...(fromPantryRowId ? { from_pantry_row_id: fromPantryRowId } : {}),
    // Which family member's user_recipes row the slug refers to. 0139.
    ...(recipeUserId ? { recipe_user_id: recipeUserId } : {}),
    // Eat-time anchor. 0153.
    ...(eatAnchor ? { eat_anchor: true } : {}),
    // Day 2 morning anchor for overnight bakes. 0154.
    ...(Number.isFinite(day2Hour) ? { day2_hour: Math.floor(day2Hour) } : {}),
    // Point-in-time recipe snapshot. 0164.
    ...(snapshotRecipe ? { recipe_snapshot: snapshotRecipe } : {}),
    // Weekly-plan back-reference. 0287.
    ...(weeklyPlanId ? { weekly_plan_id: weeklyPlanId } : {}),
  };
  const { data, error: e } = await supabase
    .from("scheduled_meals")
    .insert(row)
    .select()
    .single();
  if (e) throw e;
  // Queue prep reminders for the cook. Fire-and-forget — failures are
  // warned inside syncPrepNotifications and never block scheduling.
  const cookUserId = data.cook_id || data.user_id;
  if (recipeResolver && cookUserId) {
    const recipe = recipeResolver(data.recipe_slug, data.recipe_user_id);
    if (recipe) {
      syncPrepNotifications({ scheduledMeal: data, recipe, userId: cookUserId });
    }
  }
  return data;
}
